import useGetTrips from '../../API/hooks/trips/useGetTrips';
import useGetTripMerge from '../../API/hooks/trips/useGetTripMerge';
import useGetVehiclesLocations from '../../API/hooks/maps/useGetVehiclesLocations';
import { useGetVehicleLocationsByIds } from '../../API/hooks/maps/useGetVehicleLocations';
import { FilterConfig } from '../../components/common/DropdownFilter/types';
import ItemsList from '../../components/common/ItemsList/ItemsList';
import Toolbar from '../../components/common/Toolbar/Toolbar';
import MapWidget from '../../components/common/Map/Map';
import GroupSelection from '../../components/GroupSelection/GroupSelection';
import S from './TripsPageStyles';
import TripsItem from './TripsItem/TripsItem';
import TripMergeControls from './TripMergeControls/TripMergeControls';
import CloseOngoingTrips from './CloseOngoingTrips/CloseOngoingTrips';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import { getTimeRangeText, getPeriodDisplayLabel } from '../../utils/dateUtils';
import { getLocalStorageKey } from '../../utils/storageUtils';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useGetTripsReport from '../../API/hooks/reports/useGetTripsReport';
import useGetCurrentPage from '../../API/hooks/useGetCurrentPage';
import TimeSelection from '../../components/common/Toolbar/TimeSelection';
import { tripsPageFilters } from '../../constants/FilterDefault';
import useLocalStorage, { useGroupFilters } from '../../API/hooks/useLocalStorage';
import useOngoingTripMapIds from '../../API/hooks/trips/useOngoingTripMapIds';

const SEARCH_CONFIG = [
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { startAt_DESC: 'Newest first' },
      { startAt_ASC: 'Oldest first' },
      { distanceInKm_ASC: 'Distance: asc' },
      { distanceInKm_DESC: 'Distance: desc' },
      { durationInMs_ASC: 'Duration: asc' },
      { durationInMs_DESC: 'Duration: desc' },
      { 'driver.firstName_ASC': 'Driver name: asc' },
      { 'driver.firstName_DESC': 'Driver name: desc' },
      { 'vehicle.licensePlateNumber_ASC': 'License plate: asc' },
      { 'vehicle.licensePlateNumber_DESC': 'License plate: desc' },
    ],
  },
] as FilterConfig[];

function TripsPage(): JSX.Element {
  const fleetId = useGetFleetId();
  const [groupFilters, setGroupFilters] = useGroupFilters();
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('trips-page-filters'), tripsPageFilters);
  const { data: fleetData } = useGetFleet(fleetId);
  const { data: tripMerge } = useGetTripMerge(fleetId);
  const {
    ongoingVehicleIds,
    ongoingTripSpeedDisplayByVehicleId,
  } = useOngoingTripMapIds(fleetId);

  const tripMergeCustomQuery = `&showStandaloneAndMergedTrips=${tripMerge?.clientTripMergeEnabled ? 'true' : 'false'}`;

  return (
    <S.Wrapper>
      <div className="header">
        <Toolbar title="Trips" />
        <div className="wrapper">
          <TimeSelection allTimeOption filters={filters} setFilters={setFilters} />
          <GroupSelection filters={groupFilters || {}} setFilters={setGroupFilters} />
        </div>
      </div>
      <div className="widgets-container">
        <MapWidget
          fleetId={fleetId}
          entityType="trip"
          getLocationsQuery={useGetVehiclesLocations}
          getLocationDetailsQuery={useGetVehicleLocationsByIds}
          groupFilters={groupFilters || {}}
          fleetOngoingVehicleIds={ongoingVehicleIds}
          ongoingTripSpeedDisplayByVehicleId={ongoingTripSpeedDisplayByVehicleId}
          showSpeedInVehicleInfo
        />
      </div>
      <ItemsList
        entity="trips"
        title={getPeriodDisplayLabel(filters)}
        fleetId={fleetId}
        filtersConfig={SEARCH_CONFIG}
        filters={filters}
        defaultFilters={tripsPageFilters}
        setFilters={setFilters}
        groupFilters={groupFilters || {}}
        getQuery={useGetTrips}
        customQuery={tripMergeCustomQuery}
        hasSearch
        hasCreateReport
        reportEndpoint={useGetTripsReport}
        reportName={`${fleetData?.name || 'fleet_na'}_trips_${getTimeRangeText(filters)}`}
        curentPage={useGetCurrentPage()}
        toolbarExtra={(
          <>
            <TripMergeControls
              fleetId={fleetId}
              filters={filters || tripsPageFilters}
              setFilters={setFilters}
            />
            <CloseOngoingTrips fleetId={fleetId} />
          </>
        )}
      >
        <TripsItem />
      </ItemsList>
    </S.Wrapper>
  );
}

export default TripsPage;
